import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, BookOpen, Languages, Trash2, Search, X, Pencil, Users, FileText, CheckCircle, XCircle, Loader, Clock, Pause, CheckSquare, Square } from 'lucide-react';
import { useMemo, useState } from 'react';
import { seriesApi } from '../api/series';
import { teamsApi } from '../api/teams';
import type { Series } from '../types';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import UserBadge from '../components/UserBadge';
import TeamMultiSelect from '../components/TeamMultiSelect';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import { useAuth } from '../contexts/useAuth';
import { useToast } from '../contexts/ToastContext';

function JobStatusDot({ status }: { status: string | null | undefined }) {
  if (!status) return null;
  const map: Record<string, { icon: typeof CheckCircle; color: string; label: string }> = {
    completed: { icon: CheckCircle, color: 'text-emerald-500', label: 'Завершено' },
    failed: { icon: XCircle, color: 'text-rose-500', label: 'Ошибка' },
    processing: { icon: Loader, color: 'text-blue-500', label: 'В работе' },
    queued: { icon: Clock, color: 'text-yellow-500', label: 'В очереди' },
    paused: { icon: Pause, color: 'text-orange-500', label: 'Пауза' },
  };
  const entry = map[status] ?? { icon: Clock, color: 'text-gray-400', label: status };
  const Icon = entry.icon;
  return (
    <span title={`Последнее задание: ${entry.label}`} className="flex items-center">
      <Icon className={`h-3.5 w-3.5 ${entry.color} ${status === 'processing' ? 'animate-spin' : ''}`} />
    </span>
  );
}

export default function SeriesListPage() {
  const { user, isAdmin } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState('ja');
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editSeriesId, setEditSeriesId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editSourceLanguage, setEditSourceLanguage] = useState('ja');
  const [editTeamIds, setEditTeamIds] = useState<string[]>([]);
  const [editError, setEditError] = useState<string | null>(null);
  const [selectedSeries, setSelectedSeries] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);

  const languageOptions = [
    { value: 'ja', label: '🇯🇵 Japanese (日本語)' },
    { value: 'en', label: '🇬🇧 English' },
    { value: 'ko', label: '🇰🇷 Korean (한국어)' },
  ];

  const query = searchParams.get('q') ?? '';
  const filterLanguage = searchParams.get('lang') ?? 'all';
  const filterTeam = searchParams.get('team') ?? 'all';
  const sort = searchParams.get('sort') ?? 'created_desc';

  const { data: series, isLoading, error } = useQuery({
    queryKey: ['series'],
    queryFn: () => seriesApi.getAll(),
    refetchInterval: 30000,
  });

  const { data: teams } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsApi.getAll(),
  });

  const createMutation = useMutation({
    mutationFn: (data: { title: string; source_language?: string; team_ids?: string[] }) =>
      seriesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['series'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setIsModalOpen(false);
      setTitle('');
      setSourceLanguage('ja');
      setTeamIds([]);
      toast('Серия создана', 'success');
    },
    onError: () => toast('Не удалось создать серию', 'error'),
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; title: string; source_language: string; team_ids: string[] }) =>
      seriesApi.update(data.id, {
        title: data.title.trim(),
        source_language: data.source_language,
        team_ids: data.team_ids,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['series'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setIsEditModalOpen(false);
      setEditSeriesId(null);
      setEditTitle('');
      setEditSourceLanguage('ja');
      setEditTeamIds([]);
      setEditError(null);
      toast('Серия обновлена', 'success');
    },
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } } | null)?.response?.status;
      if (status === 409) {
        setEditError('Серия с таким названием и исходным языком уже существует');
        return;
      }
      if (status === 404) {
        setEditError('Серия не найдена (возможно, уже удалена)');
        return;
      }
      setEditError('Не удалось обновить серию');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => seriesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['series'] });
      toast('Серия удалена', 'info');
    },
    onError: () => toast('Не удалось удалить серию', 'error'),
  });

  const handleDelete = (id: string, title: string) => {
    if (window.confirm(`Вы уверены, что хотите удалить серию "${title}"? Это действие нельзя отменить, и будут удалены все связанные данные (главы, глоссарий и т.д.).`)) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      title: title.trim(),
      source_language: sourceLanguage,
      ...(teamIds.length > 0 ? { team_ids: teamIds } : {}),
    });
  };

  const openEdit = (item: Series) => {
    setEditSeriesId(item.id);
    setEditTitle(item.title ?? '');
    setEditSourceLanguage(item.source_language ?? 'ja');
    setEditTeamIds(item.teams?.map(t => t.id) ?? []);
    setEditError(null);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editSeriesId) return;
    updateMutation.mutate({
      id: editSeriesId,
      title: editTitle,
      source_language: editSourceLanguage,
      team_ids: editTeamIds,
    });
  };

  const toggleSeriesSelection = (id: string) => {
    setSelectedSeries(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = () => {
    const ids = Array.from(selectedSeries);
    if (ids.length === 0) return;
    if (!window.confirm(`Удалить ${ids.length} серий? Это действие нельзя отменить, и будут удалены все связанные данные.`)) return;
    ids.forEach(id => deleteMutation.mutate(id));
    setSelectedSeries(new Set());
    setSelectionMode(false);
  };

  const seriesArray = useMemo(() => (Array.isArray(series) ? series : []), [series]);

  const filteredSeries = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    const tokens = q ? q.split(/\s+/).filter(Boolean) : [];

    const matchesQuery = (item: { id: string; title: string }) => {
      if (tokens.length === 0) return true;
      const haystack = `${item.title} ${item.id}`.toLowerCase();
      return tokens.every((t) => haystack.includes(t));
    };

    const matchesLanguage = (item: { source_language: string }) => {
      if (!filterLanguage || filterLanguage === 'all') return true;
      return String(item.source_language || '').toLowerCase() === String(filterLanguage).toLowerCase();
    };

    const matchesTeam = (item: Series) => {
      if (!filterTeam || filterTeam === 'all') return true;
      if (filterTeam === 'none') return !item.teams?.length;
      return item.teams?.some(t => t.id === filterTeam) ?? false;
    };

    const items = seriesArray.filter((item) => matchesQuery(item) && matchesLanguage(item) && matchesTeam(item));

    const compareTitle = (a: { title: string }, b: { title: string }) =>
      String(a.title || '').localeCompare(String(b.title || ''), undefined, { sensitivity: 'base' });

    const compareCreated = (a: { created_at: string }, b: { created_at: string }) => {
      const ad = new Date(a.created_at).getTime();
      const bd = new Date(b.created_at).getTime();
      if (!Number.isFinite(ad) && !Number.isFinite(bd)) return 0;
      if (!Number.isFinite(ad)) return 1;
      if (!Number.isFinite(bd)) return -1;
      return ad - bd;
    };

    const sorted = [...items];
    switch (sort) {
      case 'created_asc':
        sorted.sort(compareCreated);
        break;
      case 'title_asc':
        sorted.sort(compareTitle);
        break;
      case 'title_desc':
        sorted.sort((a, b) => compareTitle(b, a));
        break;
      case 'created_desc':
      default:
        sorted.sort((a, b) => compareCreated(b, a));
        break;
    }

    return sorted;
  }, [filterLanguage, filterTeam, query, seriesArray, sort]);

  const setParam = (key: string, value: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const trimmed = typeof value === 'string' ? value.trim() : null;
        if (trimmed) next.set(key, trimmed);
        else next.delete(key);
        return next;
      },
      { replace: true }
    );
  };

  const setParamWithDefault = (key: string, value: string, defaultValue: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === defaultValue) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true }
    );
  };

  const resetFilters = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('q');
        next.delete('lang');
        next.delete('team');
        next.delete('sort');
        return next;
      },
      { replace: true }
    );
  };

  const hasActiveFilters =
    (query && query.trim()) || (filterLanguage && filterLanguage !== 'all') || (filterTeam && filterTeam !== 'all') || (sort && sort !== 'created_desc');

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="Ошибка загрузки серий" />;

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <button
          type="button"
          onClick={() => navigate('/jobs')}
          className="text-left text-3xl font-bold text-gray-900 dark:text-white hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          title="Перейти к задачам"
        >
          Список комиксов
        </button>
        <div className="flex gap-2 w-full sm:w-auto">
          {isAdmin && (
            <Button
              variant={selectionMode ? 'primary' : 'secondary'}
              className="flex items-center justify-center"
              onClick={() => { setSelectionMode(!selectionMode); setSelectedSeries(new Set()); }}
            >
              {selectionMode ? <CheckSquare className="h-4 w-4 mr-1" /> : <Square className="h-4 w-4 mr-1" />}
              Выбор
            </Button>
          )}
          <Button className="flex-1 sm:flex-initial flex items-center justify-center" onClick={() => setIsModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Создать серию
          </Button>
        </div>
      </div>

      {selectionMode && selectedSeries.size > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
          <span className="text-sm font-medium text-primary-700 dark:text-primary-300">Выбрано: {selectedSeries.size}</span>
          <Button size="sm" variant="secondary" onClick={handleBulkDelete} disabled={deleteMutation.isPending}>
            <Trash2 className="h-4 w-4 mr-1" />
            Удалить выбранные
          </Button>
          <Button size="sm" variant="secondary" onClick={() => { setSelectedSeries(new Set()); setSelectionMode(false); }}>
            Отмена
          </Button>
        </div>
      )}

      {seriesArray.length > 0 && (
        <Card className="mb-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Поиск
              </label>
              <div className="relative">
                <Input
                  value={query}
                  onChange={(e) => setParam('q', e.target.value)}
                  placeholder="Название серии или ID…"
                  className="pl-10 pr-10"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                {query.trim() && (
                  <button
                    type="button"
                    onClick={() => setParam('q', '')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded"
                    title="Очистить поиск"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="min-w-[220px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Язык
              </label>
              <select
                value={filterLanguage}
                onChange={(e) => setParamWithDefault('lang', e.target.value, 'all')}
                className="w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
              >
                <option value="all">Все языки</option>
                {languageOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {teams && teams.length > 0 && (
              <div className="min-w-[220px]">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Команда
                </label>
                <select
                  value={filterTeam}
                  onChange={(e) => setParamWithDefault('team', e.target.value, 'all')}
                  className="w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                >
                  <option value="all">Все команды</option>
                  <option value="none">Без команды</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="min-w-[220px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Сортировка
              </label>
              <select
                value={sort}
                onChange={(e) => setParamWithDefault('sort', e.target.value, 'created_desc')}
                className="w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
              >
                <option value="created_desc">Новые сначала</option>
                <option value="created_asc">Старые сначала</option>
                <option value="title_asc">Название: А → Я</option>
                <option value="title_desc">Название: Я → А</option>
              </select>
            </div>

            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={resetFilters}
                disabled={!hasActiveFilters}
                title="Сбросить фильтры"
              >
                Сбросить
              </Button>
            </div>
          </div>

          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Показано {filteredSeries.length} из {seriesArray.length}
          </div>
        </Card>
      )}

      {seriesArray.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              Нет серий
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Начните с создания новой серии
            </p>
          </div>
        </Card>
      ) : filteredSeries.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              Ничего не найдено
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Попробуйте изменить запрос или сбросить фильтры
            </p>
            <div className="mt-4">
              <Button variant="secondary" onClick={resetFilters} disabled={!hasActiveFilters}>
                Сбросить фильтры
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredSeries.map((item) => (
            <Card key={item.id} className={`hover:shadow-md transition-shadow ${selectionMode && selectedSeries.has(item.id) ? 'ring-2 ring-primary-500' : ''}`}>
              <div className="flex items-start justify-between">
                {selectionMode && (
                  <input
                    type="checkbox"
                    checked={selectedSeries.has(item.id)}
                    onChange={() => toggleSeriesSelection(item.id)}
                    className="h-4 w-4 mt-1 mr-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-500 dark:bg-gray-600 flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/series/${item.id}`}
                    className="block mb-2"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors cursor-pointer truncate">
                      {item.title}
                    </h3>
                  </Link>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center">
                      <Languages className="h-4 w-4 mr-1" />
                      {item.source_language.toUpperCase()}
                    </span>
                    {typeof item.chapter_count === 'number' && item.chapter_count > 0 && (
                      <span className="flex items-center">
                        <FileText className="h-3.5 w-3.5 mr-1" />
                        {item.chapter_count} {item.chapter_count === 1 ? 'глава' : item.chapter_count < 5 ? 'главы' : 'глав'}
                      </span>
                    )}
                    {item.last_job_status && <JobStatusDot status={item.last_job_status} />}
                    {item.teams && item.teams.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {item.teams.map((t) => (
                          <span
                            key={t.id}
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                          >
                            {t.name}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <UserBadge username={item.created_by_username} />
                    {item.created_at && (
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {formatRelativeTime(item.created_at)}
                      </span>
                    )}
                  </div>
                </div>
                {(isAdmin || item.created_by_user_id === user?.id) && (
                <div className="ml-2 flex flex-col items-end gap-1">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDelete(item.id, item.title);
                    }}
                    disabled={deleteMutation.isPending}
                    className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    title="Удалить серию"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openEdit(item);
                    }}
                    disabled={updateMutation.isPending}
                    className="p-2 text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
                    title="Редактировать серию"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Создать новую серию"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!title.trim() || createMutation.isPending}
            >
              Создать
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Название серии"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            placeholder="Введите название серии"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Исходный язык
            </label>
            <select
              value={sourceLanguage}
              onChange={(e) => setSourceLanguage(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
            >
              {languageOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {teams && teams.length > 0 && (
            <TeamMultiSelect
              teams={teams}
              selectedIds={teamIds}
              onChange={setTeamIds}
              label="Команды"
            />
          )}
        </form>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditSeriesId(null);
          setEditError(null);
        }}
        title="Редактировать серию"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setIsEditModalOpen(false);
                setEditSeriesId(null);
                setEditError(null);
              }}
            >
              Отмена
            </Button>
            <Button
              onClick={handleEditSubmit}
              disabled={!editTitle.trim() || updateMutation.isPending || !editSeriesId}
            >
              Сохранить
            </Button>
          </>
        }
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          {editError && <ErrorMessage message={editError} />}
          <Input
            label="Название серии"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            required
            placeholder="Введите название серии"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Исходный язык
            </label>
            <select
              value={editSourceLanguage}
              onChange={(e) => setEditSourceLanguage(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
            >
              {languageOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {teams && teams.length > 0 && (
            <TeamMultiSelect
              teams={teams}
              selectedIds={editTeamIds}
              onChange={setEditTeamIds}
              label="Команды"
            />
          )}
        </form>
      </Modal>
    </div>
  );
}
